/**
 * Password validation utilities for GiddyCarrot.
 *
 * Supabase project rules require:
 *  - Minimum 8 characters
 *  - At least one uppercase letter
 *  - At least one lowercase letter
 *  - At least one digit
 *
 * These helpers let both the signup form and the reset-password page
 * share validation logic and display consistent requirement indicators.
 */

export const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'One number', test: (pw) => /\d/.test(pw) },
];

/**
 * Validate a password against all rules.
 * @param {string} password
 * @returns {{ valid: boolean, results: Array<{id:string, label:string, passed:boolean}> }}
 */
export const validatePassword = (password) => {
  const pw = typeof password === 'string' ? password : '';
  const results = PASSWORD_RULES.map((rule) => ({
    id: rule.id,
    label: rule.label,
    passed: rule.test(pw),
  }));
  return {
    valid: results.every((r) => r.passed),
    results,
  };
};

/**
 * Build a user-friendly error string listing all unmet requirements.
 * @param {string} password
 * @returns {string} Empty string when all requirements are met.
 */
export const getPasswordErrorMessage = (password) => {
  const { valid, results } = validatePassword(password);
  if (valid) return '';
  const failed = results.filter((r) => !r.passed).map((r) => r.label.toLowerCase());
  return `Password must have: ${failed.join(', ')}.`;
};
